// src/services/supplierService.js
/**
 * Supplier Service - Handles all supplier-related API communication with backend
 * Backend API Base URL: Production VPS via config.js
 */

import axios from 'axios';
import { NODE_SUPPLIERS_API_URL } from '../config';

const SUPPLIER_API_URL = `${NODE_SUPPLIERS_API_URL}/suppliers`;

/**
 * Get all suppliers with pagination and optional filters
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Records per page (default: 50, max: 500)
 * @param {Object} filters - Optional filters { estado, condicionImpositiva }
 * @returns {Promise} Response with suppliers list and pagination
 */
export const getAllSuppliers = async (page = 1, pageSize = 50, filters = {}) => {
    try {
        const params = {
            page,
            pageSize
        };

        // Add optional filters
        if (filters.estado) {
            params.estado = filters.estado;
        }
        if (filters.condicionImpositiva) {
            params.condicionImpositiva = filters.condicionImpositiva;
        }

        const response = await axios.get(SUPPLIER_API_URL, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching all suppliers:', error);
        throw error;
    }
};

/**
 * Search suppliers by name (partial match)
 * @param {string} searchTerm - Name to search for
 * @param {number} maxResults - Maximum results to return (default: 50)
 * @returns {Promise} Response with matching suppliers
 */
export const searchSuppliersByName = async (searchTerm, maxResults = 50) => {
    try {
        if (!searchTerm || searchTerm.trim().length === 0) {
            return { results: [], total_count: 0 };
        }

        const response = await axios.get(
            `${SUPPLIER_API_URL}/search-name/${encodeURIComponent(searchTerm)}`,
            { params: { maxResults } }
        );
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 404) {
            return { results: [], total_count: 0 };
        }
        console.error('Error searching suppliers by name:', error);
        throw error;
    }
};

/**
 * Search supplier by CUIT (exact match)
 * @param {string} cuit - CUIT number
 * @returns {Promise} Response with supplier data or null
 */
export const searchSupplierByCuit = async (cuit) => {
    try {
        const cleanCuit = cuit.replace(/-/g, ''); // Remove hyphens
        const response = await axios.get(`${SUPPLIER_API_URL}/search-cuit/${cleanCuit}`);
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 404) {
            return null;
        }
        console.error('Error searching supplier by CUIT:', error);
        throw error;
    }
};

/**
 * Create new supplier
 * @param {Object} supplierData - Supplier data object
 * @returns {Promise} Response with created supplier
 */
export const createSupplier = async (supplierData) => {
    try {
        const response = await axios.post(`${SUPPLIER_API_URL}/save`, {
            documento_nro: supplierData.documento_nro,
            documento_tipo: supplierData.documento_tipo || 'CUIT',
            razon_social: supplierData.razon_social,
            condicion_impositiva: supplierData.condicion_impositiva,
            direccion: supplierData.direccion || '',
            localidad: supplierData.localidad || '',
            provincia: supplierData.provincia || '',
            codigopostal: supplierData.codigopostal || '',
            email: supplierData.email || '',
            telefono: supplierData.telefono || '',
            estado: supplierData.estado || 'ACTIVO',
            related_brands: supplierData.related_brands || [],
            related_categories: supplierData.related_categories || []
        });
        return response.data;
    } catch (error) {
        console.error('Error creating supplier:', error);
        throw error;
    }
};

/**
 * Update existing supplier
 * @param {number} supplierId - Supplier ID
 * @param {Object} updates - Fields to update
 * @returns {Promise} Response with updated supplier
 */
export const updateSupplier = async (supplierId, updates) => {
    try {
        const response = await axios.put(`${SUPPLIER_API_URL}/${supplierId}`, updates);
        return response.data;
    } catch (error) {
        console.error('Error updating supplier:', error);
        throw error;
    }
};
